"use client";

import { useEffect, useRef, useSyncExternalStore } from "react";

import { V2_ENDPOINT } from "@/consts";

/** How often to re-sync with the server (ms). */
const SYNC_INTERVAL_MS = 60_000;

export interface PiggybackedTime {
  serverTime: string;
  requestStartMs: number;
}

// ---------------------------------------------------------------------------
// Module-level singleton: one sync cycle shared by all hook instances.
// ---------------------------------------------------------------------------

let globalOffset = 0;
let subscriberCount = 0;
let syncTimer: ReturnType<typeof setInterval> | null = null;
const listeners = new Set<() => void>();

function getSnapshot(): number {
  return globalOffset;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  subscriberCount++;

  // Start the sync cycle when the first subscriber mounts
  if (subscriberCount === 1) {
    syncFromServer();
    syncTimer = setInterval(syncFromServer, SYNC_INTERVAL_MS);
  }

  return () => {
    listeners.delete(listener);
    subscriberCount--;

    // Stop when no subscribers remain
    if (subscriberCount === 0 && syncTimer !== null) {
      clearInterval(syncTimer);
      syncTimer = null;
    }
  };
}

function emitChange(): void {
  for (const listener of listeners) {
    listener();
  }
}

function applyOffset(serverTimeStr: string, requestStartMs?: number): void {
  const serverMs = new Date(serverTimeStr).getTime();
  if (isNaN(serverMs)) return;

  let clientNowMs: number;
  if (requestStartMs !== undefined) {
    // Account for network latency: assume server timestamp was generated at
    // the midpoint of the round-trip (NTP-style).
    const rtt = Date.now() - requestStartMs;
    clientNowMs = requestStartMs + rtt / 2;
  } else {
    clientNowMs = Date.now();
  }

  globalOffset = serverMs - clientNowMs;
  emitChange();
}

async function syncFromServer(): Promise<void> {
  try {
    const start = Date.now();
    const res = await fetch(`${V2_ENDPOINT}/v1/time`);
    if (!res.ok) return;
    const json = (await res.json()) as { serverTime: string };
    applyOffset(json.serverTime, start);
  } catch {
    // Network failure — keep existing offset
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Returns the offset (ms) between the server clock and the client clock:
 *   `offset = serverTimeMs - clientTimeMs`
 *
 * To get the current "server now": `new Date(Date.now() + offset)`
 *
 * A single background sync cycle (GET /v1/time) is shared across all
 * component instances. Piggybacked serverTime from other API responses
 * can be passed in to opportunistically refine the offset.
 */
export function useServerTimeOffset(piggybacked?: PiggybackedTime | null): number {
  const offset = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  // Opportunistically refine offset from piggy-backed serverTime.
  const lastPiggybackedRef = useRef<string | null>(null);
  useEffect(() => {
    if (piggybacked && piggybacked.serverTime !== lastPiggybackedRef.current) {
      lastPiggybackedRef.current = piggybacked.serverTime;
      applyOffset(piggybacked.serverTime, piggybacked.requestStartMs);
    }
  }, [piggybacked]);

  return offset;
}

/**
 * Returns a Date representing the server's current time, adjusted by the given
 * offset (from `useServerTimeOffset`).
 */
export function getServerNow(offsetMs: number): Date {
  return new Date(Date.now() + offsetMs);
}
